import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux';
import { scrollToTop } from '../../../utils/helpers';
import {
  COUNTRY_CODES,
  findCountryByIso,
  pickCountryIso,
  formatPhoneDisplay,
  parsePhoneInput,
} from '../../../utils/countryCodes';

// NEW — onCancelClick: called when the user clicks "Cancel" while editing.
// The parent should reset isEditing to false AND discard whatever's in
// updatedData for this section (so a half-typed phone number doesn't
// silently stick around if the user reopens Edit) — see the note below
// the component for the exact shape expected.
const ContactDetails = ({ isEditing, onEditClick, onSaveClick, onCancelClick, onDataChange, updatedData }) => {
  const { userInfo } = useSelector(state => state.user);

  const getValue = (field) => {
    return updatedData?.[field] !== undefined ? updatedData[field] : userInfo?.[field];
  };

  const [selectedIso, setSelectedIso] = useState(() =>
    pickCountryIso(getValue('phone_country_code'), userInfo?.country)
  );

  useEffect(() => {
    if (!isEditing) return;
    const stored = getValue('phone_country_code');
    const iso = pickCountryIso(stored, userInfo?.country);
    setSelectedIso(iso);
    const country = findCountryByIso(iso);
    if (country && !stored) {
      onDataChange('phone_country_code', country.dial);
    }
  }, [isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCodeChange = (e) => {
    const iso = e.target.value;
    setSelectedIso(iso);
    const country = findCountryByIso(iso);
    onDataChange('phone_country_code', country ? country.dial : '');
  };

  const handlePhoneChange = (e) => {
    const parsed = parsePhoneInput(e.target.value, userInfo?.country);
    onDataChange('phone', parsed.phone);
    if (parsed.dial) {
      setSelectedIso(parsed.iso);
      onDataChange('phone_country_code', parsed.dial);
    }
  };

  // Resets the dropdown back to whatever's actually saved (undoing any
  // selection made while editing) before handing off to the parent's
  // cancel handler, which should flip isEditing back to false.
  const handleCancel = () => {
    setSelectedIso(pickCountryIso(userInfo?.phone_country_code, userInfo?.country));
    if (typeof onCancelClick === 'function') {
      onCancelClick();
    } else {
      // Fallback so this doesn't silently do nothing if the parent hasn't
      // been updated yet — same effect as Edit toggling off.
      console.warn('ContactDetails: onCancelClick prop not provided — pass it from the parent to reset isEditing to false.');
      onEditClick();
    }
  };

  return (
    <div>
      <div className="section-title">
        Contact Details{" "}
        {isEditing ? (
          // NEW — Cancel sits next to Save, same "small float-end" styling,
          // with a little left margin so they don't touch.
          <>
          <a onClick={handleCancel} className="small float-end cursor-pointer" >
              Cancel
            </a>
            <a onClick={onSaveClick} className="small float-end cursor-pointer" style={{ marginRight: 12 }}>
              Save
            </a>
            
          </>
        ) : (
          <a onClick={onEditClick} className="small float-end cursor-pointer">
            Edit
          </a>
        )}
      </div>
      <div className="row py-3 border-top">
        <div className="col-md-6 pe-md-4">
          <table className="table table-borderless table-sm mini-data mb-0">
            <tbody>
              <tr>
                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', width: 60 }}>Mobile:</td>
                <td>{isEditing ? (
                  <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 6, alignItems: 'center' }}>
                    <select
                      aria-label="Country code"
                      value={selectedIso}
                      onChange={handleCodeChange}
                      className="form-select form-select-sm"
                      style={{ width: 130, flex: '0 0 130px' }}
                    >
                      {!selectedIso && <option value="">Code</option>}
                      {COUNTRY_CODES.map(c => (
                        <option key={c.iso} value={c.iso}>
                          {c.name} ({c.dial})
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      inputMode="tel"
                      aria-label="Phone number"
                      placeholder="Number without country code"
                      value={getValue('phone') || ''}
                      onChange={handlePhoneChange}
                      className="form-control form-control-sm"
                      style={{ width: 150, flex: '0 0 150px' }}
                    />
                  </div>
                ) : (
                  formatPhoneDisplay({
                    phone: userInfo?.phone,
                    phoneCountryCode: userInfo?.phone_country_code,
                    country: userInfo?.country,
                  }) || 'Not added'
                )}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="col-md-6 pe-md-4"></div>
      </div>
      <div className="text-end mt-0">
        <button type='button' className="btn btn-link back-to-top" onClick={scrollToTop}>
          Back to Top <i className="fa fa-angle-up" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  )
}

export default ContactDetails